import { PropsWithChildren, createContext, useContext, useState } from 'react';

type DrawerName = string;

type DrawerContextType<TDrawersConfig extends string | number | symbol = string> = {
  [K in TDrawersConfig as `open${Capitalize<string & K>}Drawer`]: () => void;
} & {
  [K in TDrawersConfig as `toggle${Capitalize<string & K>}Drawer`]: () => void;
} & {
  [K in TDrawersConfig as `is${Capitalize<string & K>}DrawerOpen`]: boolean;
} & {
  openedDrawer: TDrawersConfig | null;
  closeDrawer: () => void;
};

export const useInitDrawers = <TDrawerNames extends DrawerName[]>(config: TDrawerNames) => {
  const [activeDrawer, setActiveDrawer] = useState<DrawerName | null>(null);

  const controls = config.reduce((acc, drawerName) => {
    const some = capitalize(drawerName);
    return {
      ...acc,
      [`is${some}DrawerOpen`]: activeDrawer === drawerName,
      [`open${some}Drawer`]: () => setActiveDrawer(drawerName),
      [`toggle${some}Drawer`]: () =>
        setActiveDrawer((prev) => (prev === drawerName ? null : drawerName))
    };
  }, {});

  return {
    openedDrawer: activeDrawer,
    ...controls,
    closeDrawer: () => setActiveDrawer(null)
  };
};

const DrawersContext = createContext<DrawerContextType>({} as DrawerContextType);

export const DrawersProvider = ({
  children,
  config
}: PropsWithChildren<{ config: DrawerName[] }>) => {
  const value = useInitDrawers(config);
  return <DrawersContext.Provider value={value}>{children}</DrawersContext.Provider>;
};

export const useDrawerUntyped = <TDrawersConfig extends string | number | symbol>() =>
  useContext(DrawersContext) as DrawerContextType<TDrawersConfig>;

function capitalize(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * @tutorial
 * isMobileAppPopupOpened -> went to useSettings
 * cookies -> went to useSettings
 *
 * isLeftDrawerOpen -> isLeftDrawerOpen
 * isTopDrawerOpen -> isTopDrawerOpen
 * ---- names were not changed!!!
 *
 * closeXDrawer -> closeDrawer
 * goToAccount -> closeDrawer
 */
